import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookConsultModal from '../components/BookConsultModal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, Shield, CheckCircle, Server, Network, Cloud, Database } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const CybersecurityPage = () => {
  const [showModal, setShowModal] = useState(false);

  const assessmentItems = [
    { title: 'Account + MFA Review', desc: 'User accounts, admin access, MFA status', icon: Shield },
    { title: 'Device Baseline Review', desc: 'Endpoint security, patching, encryption', icon: Server },
    { title: 'Microsoft 365 Security', desc: 'Security settings, policies, sharing', icon: Cloud },
    { title: 'Backup + Recovery', desc: 'Backup status, recovery testing, gaps', icon: Database },
    { title: 'Network Exposure', desc: 'External exposure, firewall, remote access', icon: Network },
    { title: 'Risk Report', desc: 'Prioritized findings + remediation plan', icon: CheckCircle }
  ];

  const securityBaseline = [
    { title: 'Multi-Factor Authentication', desc: 'Required for all users, especially admins' },
    { title: 'Conditional Access', desc: 'Location and device-based access controls' },
    { title: 'Admin Account Protection', desc: 'Separate admin accounts, limited privileges' },
    { title: 'Device Policies', desc: 'Encryption, screen lock, update requirements' },
    { title: 'Backup Verification', desc: 'Regular backup testing and monitoring' },
    { title: 'Security Monitoring', desc: 'Sign-in monitoring and alert review' }
  ];

  const reportDeliverables = [
    { title: 'Executive Summary', desc: 'High-level overview for leadership' },
    { title: 'Risk List with Severity', desc: 'Prioritized findings by impact' },
    { title: 'Remediation Plan', desc: 'Step-by-step fix recommendations' },
    { title: '30/60/90-Day Roadmap', desc: 'Phased improvement plan' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-cyan-500 uppercase tracking-wide mb-2">Security-First Approach</p>
            <h1 className="text-5xl font-bold text-gray-900 mb-4">Cybersecurity for Small Business</h1>
            <p className="text-xl text-gray-600 mb-8">
              Protect your organization with practical, right-sized security solutions.
            </p>
            <div className="flex justify-center gap-4">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowModal(true)} data-testid="cyber-hero-consult">
                <Calendar className="w-5 h-5 mr-2" />
                Book a Free Consult
              </Button>
            </div>
            <p className="text-sm text-gray-600 mt-4">
              We respond within 1 business day (often faster). Emergency support available.
            </p>
          </div>
          <div className="mt-12">
            <img
              src="https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg?auto=compress&cs=tinysrgb&w=800"
              alt="Modern server room with network security"
              className="w-full h-96 object-cover rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Cyber Assessment Pricing */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-cyan-500 text-white mb-4">One-Time Service</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Cyber Assessment</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A comprehensive evaluation of your security posture with actionable recommendations. Perfect for organizations that want to understand their risk before committing to ongoing services.
            </p>
          </div>

          <Card className="border-2 border-blue-200 shadow-xl">
            <CardHeader className="text-center bg-gradient-to-br from-blue-50 to-cyan-50">
              <div className="text-5xl font-bold text-gray-900 mb-2">$250<span className="text-2xl text-gray-600">/user</span></div>
              <p className="text-gray-600">One-time assessment</p>
              <Button className="mt-4 bg-blue-600 hover:bg-blue-700" onClick={() => setShowModal(true)} data-testid="cyber-assessment-consult">
                Schedule Assessment
              </Button>
            </CardHeader>
            <CardContent className="pt-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">What You Receive</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {reportDeliverables.map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Assessment Includes */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Cyber Assessment Includes</h2>
          <p className="text-lg text-gray-600 text-center mb-12">
            A thorough review of your security across all critical areas
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assessmentItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border border-gray-200 hover:shadow-lg transition-all">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Security Baseline */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Security Baseline</h2>
          <p className="text-lg text-gray-600 text-center mb-12">
            For managed IT clients, we implement these security fundamentals from day one
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {securityBaseline.map((item, index) => (
              <Card key={index} className="border border-gray-200">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Improve Your Security?</h2>
          <p className="text-xl text-blue-50 mb-8">
            Start with an assessment or discuss ongoing security management.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100" onClick={() => setShowModal(true)} data-testid="cyber-cta-consult">
              <Calendar className="w-5 h-5 mr-2" />
              Book a Free Consult
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <BookConsultModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};

export default CybersecurityPage;
